import { useCallback, useEffect, useRef, useState } from "react";
import { type LiveNoteUpload, startLiveUpload } from "../../lib/liveNoteUpload";
import type { NoteApi } from "../../lib/noteUpload";
import { atLimit, type Clock, pauseClock, recordedMs, recordedSeconds, resumeClock, startClock } from "./clock";
import { allDevices, type DeviceChoice, type DeviceLists, inUse, loadChoice, recordingType, saveChoice, streamRequest, toDevices, VIDEO_BITS_PER_SECOND } from "./devices";

export type RecState = "ready" | "rec" | "paused" | "review";

/** A finished recording, played back in the sheet until it is used or retaken. */
export interface Recorded {
  blob: Blob;
  seconds: number;
  url: string;
}

const closeTracks = (s: MediaStream | null) => {
  for (const t of s?.getTracks() ?? []) t.stop();
};

const OPEN_FAILED = "We couldn't open your camera. Allow it for this page, then try again.";
const NO_CAMERA = "This browser can't record a video.";

/**
 * One video recording from the camera to the finished file: the devices, start, pause, resume and stop, and the
 * clock that counts only recorded time. `limitSec` is the side's own limit. With `upload`, each full part of the take goes
 * up while it is recorded (src/lib/liveNoteUpload.ts); `hand` gives that upload to whoever sends the take, and a take
 * not handed on is cancelled when it is retaken or the recorder is left.
 *
 * Why a generation count: opening or switching a device waits for the browser, and the person can leave or start
 * recording meanwhile. Every change of state bumps the count, and a device that arrives late sees it moved on and
 * is closed instead of used. Why devices change only before recording: a recorder stops itself when the tracks of
 * its stream change (the MediaStream Recording spec), so a swap mid-take would end the take.
 */
export function useVideoRecorder(limitSec: number, upload?: NoteApi) {
  const [status, setStatusState] = useState<RecState>("ready");
  const [problem, setProblem] = useState("");
  const [ready, setReady] = useState(false);
  const [live, setLive] = useState<MediaStream | null>(null);
  const [devices, setDevices] = useState<DeviceLists>({ cameras: [], mics: [] });
  const [chosen, setChosen] = useState<DeviceChoice>({});
  const [shown, setShown] = useState(0);
  const [recorded, setRecorded] = useState<Recorded | null>(null);

  const state = useRef<RecState>("ready");
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const clock = useRef<Clock>({ before: 0, since: null });
  const tick = useRef<number | undefined>(undefined);
  const gen = useRef(0);
  const url = useRef<string | null>(null);
  const choice = useRef<DeviceChoice>({});
  const liveUp = useRef<LiveNoteUpload | null>(null);

  const setStatus = useCallback((next: RecState) => {
    state.current = next;
    setStatusState(next);
  }, []);

  const adopt = useCallback(async (s: MediaStream) => {
    stream.current = s;
    setLive(s);
    const all = await allDevices();
    const lists = toDevices(all);
    setDevices(lists);
    const next = { camera: inUse(lists.cameras, all, s.getVideoTracks()[0]), mic: inUse(lists.mics, all, s.getAudioTracks()[0]) };
    choice.current = next;
    setChosen(next);
  }, []);

  useEffect(() => {
    const mine = ++gen.current;
    if (!navigator.mediaDevices?.getUserMedia) {
      setProblem(NO_CAMERA);
      return;
    }
    const saved = loadChoice();
    const remembered = saved.camera || saved.mic;
    // Why the fallback: a device chosen last time may be unplugged, and then the browser's own pick is better than none.
    (remembered
      ? navigator.mediaDevices.getUserMedia(streamRequest(saved)).catch(() => navigator.mediaDevices.getUserMedia(streamRequest({})))
      : navigator.mediaDevices.getUserMedia(streamRequest({}))
    )
      .then(async (s) => {
        if (mine !== gen.current) return closeTracks(s);
        await adopt(s);
        if (mine === gen.current) setReady(true);
      })
      .catch(() => mine === gen.current && setProblem(OPEN_FAILED));
    return () => {
      gen.current += 1;
      window.clearInterval(tick.current);
      const r = recorder.current;
      if (r) {
        r.onstop = null;
        if (r.state !== "inactive") r.stop();
      }
      recorder.current = null;
      void liveUp.current?.abort();
      liveUp.current = null;
      closeTracks(stream.current);
      stream.current = null;
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = null;
    };
  }, [adopt]);

  const finish = useCallback(() => {
    window.clearInterval(tick.current);
    const r = recorder.current;
    if (!r || r.state === "inactive") return;
    clock.current = pauseClock(clock.current, Date.now());
    r.stop();
  }, []);

  const runTick = useCallback(() => {
    window.clearInterval(tick.current);
    // Why the clock reads the time that has passed: a tick the browser holds back never slows it down.
    tick.current = window.setInterval(() => {
      const now = Date.now();
      if (atLimit(clock.current, now, limitSec)) finish();
      else setShown(recordedSeconds(clock.current, now));
    }, 200);
  }, [finish, limitSec]);

  const start = useCallback(() => {
    const s = stream.current;
    if (!s || typeof MediaRecorder === "undefined" || state.current !== "ready") return;
    gen.current += 1;
    const type = recordingType((t) => MediaRecorder.isTypeSupported(t));
    const r = new MediaRecorder(s, { ...(type ? { mimeType: type } : {}), videoBitsPerSecond: VIDEO_BITS_PER_SECOND });
    chunks.current = [];
    const going = upload ? startLiveUpload(upload, r.mimeType || type || "video/webm") : null;
    liveUp.current = going;
    r.ondataavailable = (e) => {
      if (!e.data.size) return;
      chunks.current.push(e.data);
      going?.push(e.data);
    };
    r.onstop = () => {
      const blob = new Blob(chunks.current, { type: r.mimeType || type || "video/webm" });
      const seconds = Math.min(limitSec, Math.max(1, Math.round(recordedMs(clock.current, Date.now()) / 1000)));
      url.current = URL.createObjectURL(blob);
      setRecorded({ blob, seconds, url: url.current });
      setStatus("review");
    };
    recorder.current = r;
    clock.current = startClock(Date.now());
    setShown(0);
    r.start(1000);
    setStatus("rec");
    runTick();
  }, [limitSec, runTick, setStatus, upload]);

  const pause = useCallback(() => {
    const r = recorder.current;
    if (state.current !== "rec" || !r) return;
    const now = Date.now();
    // Why stop instead: a press that lands as the limit is reached must end the take, not hold it a hair under.
    if (atLimit(clock.current, now, limitSec)) {
      finish();
      return;
    }
    window.clearInterval(tick.current);
    clock.current = pauseClock(clock.current, now);
    r.pause();
    setShown(recordedSeconds(clock.current, now));
    setStatus("paused");
  }, [finish, limitSec, setStatus]);

  const resume = useCallback(() => {
    const r = recorder.current;
    if (state.current !== "paused" || !r) return;
    clock.current = resumeClock(clock.current, Date.now());
    r.resume();
    setStatus("rec");
    runTick();
  }, [runTick, setStatus]);

  const retake = useCallback(() => {
    void liveUp.current?.abort();
    liveUp.current = null;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
    recorder.current = null;
    setRecorded(null);
    setStatus("ready");
  }, [setStatus]);

  const hand = useCallback(() => {
    const going = liveUp.current;
    liveUp.current = null;
    return going;
  }, []);

  const pick = useCallback(
    async (kind: keyof DeviceChoice, id: string) => {
      if (state.current !== "ready" || choice.current[kind] === id) return;
      const mine = ++gen.current;
      const next = { ...choice.current, [kind]: id };
      setReady(false);
      // Why the old one closes first: some phones cannot open a second camera while the first is held.
      closeTracks(stream.current);
      const open = (c: DeviceChoice) => navigator.mediaDevices.getUserMedia(streamRequest(c));
      let s: MediaStream;
      let kept = false;
      try {
        s = await open(next);
        kept = true;
      } catch {
        if (mine !== gen.current) return;
        try {
          s = await open(choice.current);
        } catch {
          setProblem(OPEN_FAILED);
          return;
        }
      }
      if (mine !== gen.current) {
        closeTracks(s);
        return;
      }
      if (kept) saveChoice(next);
      await adopt(s);
      if (mine === gen.current) setReady(true);
    },
    [adopt],
  );

  return { status, problem, ready, live, devices, chosen, shown, recorded, start, pause, resume, stop: finish, retake, hand, pick };
}
